import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ENV } from "@app/env";
import { AngularFireAuth } from "angularfire2/auth";
import { AngularFireDatabase } from "angularfire2/database";
import * as firebase from "firebase/app";
import { Observable } from "rxjs/Observable";
import { of } from "rxjs/observable/of";
import { catchError, map, retry, switchMap, tap, take } from "rxjs/operators";
import { IBackendProcess } from "shared/models/backend-process.model";
import { IBackendResponse } from "../shared/models/backend.model";
import { IAdministratorUser, IUser, IUserStats } from "../shared/models/user.model";
import { AuthData } from "./auth";
import { CategoriesData } from "./categories";
import { EventData } from "./event";
import { HistoryData } from "./history";
import { _TRACKING_ENTITIES, TrackingProvider } from "./tracking";
import { Utilities } from "./utilities";
import { IClient } from "shared/models/client.model";

@Injectable()
export class UserData {

  get defaultTemplate() {
    // tslint:disable-next-line:max-line-length
    return "https://firebasestorage.googleapis.com/v0/b/rebus-v4.appspot.com/o/EmailTemplates%2Frefunde_rebus.html?alt=media&token=2db0e471-7569-4943-9c77-8e5f05e0c6de";
  }

  private action: string = "update";

  constructor(
    private afDb: AngularFireDatabase,
    private authService: AuthData,
    private historyData: HistoryData,
    private http: HttpClient,
    private eventData: EventData,
    private utilities: Utilities,
  ) { }

  public getLogin(equalTo): any {
    return firebase.database()
      .ref("/AdministratorList")
      .orderByChild("email")
      .equalTo(equalTo)
      .limitToFirst(1);
  }

  public resetUser(_id: string, passwordHash: string) {
    const headers = new HttpHeaders()
      .set("Content-Type", "application/json");
    return this.http.post(`${ENV.backend}/users/update`, { _id, passwordHash }, { headers });
  }
  /**
   * For backendProcess MassiveUsers
   * @param client client
   * @param process proceso de carga masiva
   */
  public createProcess(client: string, process: any) {
    process.id = this.utilities.generateID();
    return new Promise((resolve, reject) => {
      return this.afDb.database.ref(`BackendProcess/${client}/${process.id}`)
        .set(process)
        .then(() => {
          resolve(process);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public getUsersMassiveLoad(client: string, processId: string): Observable<IBackendProcess> {
    return this.afDb.object<IBackendProcess>(`BackendProcess/${client}/${processId}`).valueChanges();
  }

  public getUserAdminByUid(uid): any {
    return this.authService.getTokenUser().pipe(
      switchMap((token: string) => {
        if (!token) {
          return of([]);
        }
        const headers = new HttpHeaders({ Authorization: token });
        return this.http.get(`${ENV.backend}/admin/${uid}`, { headers });
      }),
    );
  }

  public getUsersByClient(clientId: string): Observable<any> {
    return this.authService.getTokenUser().pipe(
      switchMap((token: string) => {
        if (!token) {
          return of([]);
        }
        const headers = new HttpHeaders({ Authorization: token });
        return this.http.get(`${ENV.backend}/users/all/${clientId}`, { headers })
          .pipe(retry(3), catchError((err) => [this.utilities.consoleDecorator("Error on list users: ", err, "red")]));
      }),
    );
    // return this.afDb.list<IUser>(`Clients/${client}/Users`).valueChanges();
  }

  public getUsersStatsByClient(clientId: string): Observable<any> {
    return this.authService.getTokenUser().pipe(
      switchMap((token: string) => {
        if (!token) {
          return of([]);
        }
        const headers = new HttpHeaders({ Authorization: token });
        return this.http.get(`${ENV.backend}/users/report/count/${clientId}`, { headers })
          .pipe(retry(3), catchError((err) => [this.utilities.consoleDecorator("Error on list users: ", err, "red")]));
      }),
    );
    // return this.afDb.list<IUser>(`Clients/${client}/Users`).valueChanges();
  }

  public getUserById(clientId: string, userId: string): Observable<any> {
    return this.authService.getTokenUser().pipe(
      switchMap((token: string) => {
        if (!token) {
          return of([]);
        }
        const headers = new HttpHeaders({ Authorization: token });
        return this.http.get(`${ENV.backend}/users/${clientId}/${userId}`, { headers })
          .pipe(retry(3), catchError((err) => [this.utilities.consoleDecorator("Error get user: ", err, "red")]));
      }),
    );
    // return this.afDb.list<IUser>(`Clients/${client}/Users`).valueChanges();
  }

  public getUsersByClientTokens(clientId: string): Observable<any> {
    return this.authService.getTokenUser().pipe(
      switchMap((token: string) => {
        if (!token) {
          return of([]);
        }
        const headers = new HttpHeaders({ Authorization: token });
        return this.http.get(`${ENV.backend}/users/all/${clientId}`, { headers })
          .pipe(retry(3), catchError((err) => [this.utilities.consoleDecorator("Error on list users: ", err, "red")]));
      }),
    );
    // return this.afDb.list<IUser>(`Clients/${client}/Users`).valueChanges();
  }

  public getUsersRegisterByClient(client: string): Observable<IUser[]> {
    return this.afDb.list<any>(`Clients/heroes/UserRegisters`).valueChanges();
  }

  public getUsersRegisterRef(clientId: string) {
    return this.afDb.list<any>(`/Clients/heroes/UserRegisters`).query.ref;
  }

  public getUsersRegister(clientId: string): Observable<any[]> {
    return this.afDb.list<any>(`/Clients/${clientId}/UserRegisters`).valueChanges();
  }

  public addUserFromRegister(clientId: string, user: any, uid: string) {
    const data = { ...user, id: uid, dni: user.id };
    return this.afDb.object(`/Clients/${clientId}/Users/${uid}`).update({
      ...data,
      created_at: firebase.database.ServerValue.TIMESTAMP,
    });
  }

  public updateStateUserRegisterApproved(clientId, user) {
    return this.afDb.object(`/Clients/${clientId}/UserRegisters/${user.id}`)
      .update({
        state: "approved",
      });
  }

  public updateStateUserRegisterRejected(clientId, user) {
    return this.afDb.object(`/Clients/${clientId}/UserRegisters/${user.id}/state`).set("rejected");
  }

  public getUserQuantityAll(clientId) {
    return this.afDb.list<any>(`/Clients/${clientId}/UserRegisters`).valueChanges();
  }

  public getUserQuantity(state, clientId): Observable<any[]> {
    return this.afDb.list<any>(`/Clients/${clientId}/UserRegisters`,
      (ref) => ref.orderByChild("state").equalTo(state))
      .valueChanges();
  }

  /**
   * Return the user Stats
   * @param clientId
   * @param userId
   * @author Sebastián Guevara
   */
  public getUserStatsPointsByUserId(clientId: string, userId: string): Observable<IUserStats> {
    return this.afDb.object<IUserStats>(`Clients/${clientId}/UserStats/${userId}`)
      .valueChanges();
  }

  public getUserStatsPointsSnapByUserId(clientId: string, userId: string) {
    return this.afDb.object<IUserStats>(`Clients/${clientId}/UserStats/${userId}`)
      .query.ref;
  }

  /**
   * Return the user events
   * @param clientId
   * @param userId
   * @author Sebastián Guevara
   */
  public getUserEventsByUserId(clientId: string, userId: string): Observable<any> {
    return this.afDb.object(`Clients/${clientId}/UserEvents/${userId}`)
      .valueChanges()
      .pipe(
        switchMap((usersEvents: any) => {
          const obs = [];
          if (usersEvents) {
            Object.keys(usersEvents).forEach((key) => {
              obs.push(this.eventData.getEventInfo(clientId, key));
            });
          }
          return usersEvents === null ? Observable.of([]) :
            Observable.combineLatest(...obs)
              .pipe(
                map((data) => {
                  this.utilities.consoleDecorator("User Events", data);
                  return data;
                }),
              );
        }),
      );
  }

  /**
   * Get the user categories
   * @param clientId
   * @param userId
   */
  public getUserCategoriesByUserId(clientId: string, userId: string): Observable<any> {
    return this.afDb.object(`Clients/${clientId}/Users/${userId}/interest`).valueChanges();
    // .pipe(switchMap((data) => {
    //   let obs = [];
    //   if (data) {
    //     Object.keys(data).forEach((key) => {
    //       obs.push(this.categoriesData.getClientCategoriesById(clientId, key));
    //     })
    //   }
    //   return (data === null) ? Observable.of([]) : Observable.combineLatest(obs)
    //     .pipe(map((data) => {
    //       this.utilities.consoleDecorator('User Interest ', data);
    //       return data;
    //     }))
    // }))
  }

  public setUserCategory(clientId: string, userId: string, categoryId: string, state: boolean) {
    return Observable.fromPromise(
      this.afDb.object(`Clients/${clientId}/Users/${userId}/interest/${categoryId}`).set((state) ? state : null));
  }

  public getUserByClient(clientId: string, userId: string) {
    return this.authService.getTokenUser().pipe(
      switchMap((token: string) => {
        if (!token) {
          return of([]);
        }
        const headers = new HttpHeaders({ Authorization: token });
        return this.http.get(`${ENV.backend}/users/${clientId}/${userId}`, { headers });
      }),
    );
  }

  public updateUserInformationByClient(client: string, id: string, userInformation: IUser): Observable<any> {
    const updateUser = new Promise((resolve, reject) => {
      userInformation.update_at = new Date().getTime();
      this.afDb.object<IUser>(`Clients/${client}/Users/${userInformation.id}`)
        .update(userInformation).then(() => {
          this.historyData.set(
            `User Application Updated`,
            "users",
            "Update",
            userInformation,
          );
          // resolve();
        }).catch((error) => {
          console.error("Something big happen", error);
          reject(error);
        });
    });
    return Observable.fromPromise(updateUser);

  }

  public deleteUserByClient(client: string, id: string): Observable<any> {
    return Observable.fromPromise(
      this.afDb.object<IUser>(`Clients/${client}/Users/${id}`).remove());
  }

  public getAdministratorUsers(): Observable<any> {
    return this.authService.getTokenUser().pipe(
      switchMap((token: string) => {
        if (!token) {
          return of([]);
        }
        const headers = new HttpHeaders({ Authorization: token });
        return this.http.get(`${ENV.backend}/admin/admin-users-profiles-data`, { headers })
          .pipe((list) => list.filter((item: any) => !item.isInactive));
      }),
    );
    // return this.afDb.list<IAdministratorUser>(`AdministratorList`).valueChanges();
  }

  public getAdministratorUser(userId: string): Observable<IAdministratorUser> {
    return this.afDb.object<IAdministratorUser>(`AdministratorList/${userId}`).valueChanges();
  }

  public createUser(user: any) {
    // console.log("user to create on backend", user);
    const payload = {
      displayName: user.name,
      email: user.email,
      password: user.password,
    };
    return this.http.post(`${ENV.backend}/users/createUser`, payload)
      .catch((err) => {
        this.utilities.consoleDecorator("Error: ", err);
        return "Error creando usuario";
      })
      .map((res: any) => res.result.uid)
      .toPromise();
  }

  public createAdministratorUser(user: any): Observable<any> {
    return this.authService.getTokenUser().pipe(
      switchMap((token: string) => {
        if (!token) {
          return of([]);
        }
        const headers = new HttpHeaders({ Authorization: token });

        const sendUser = user;
        delete (sendUser.confirmPassword);
        delete (sendUser._id);

        return this.http.post(`${ENV.backend}/admin/form`, { ...sendUser }, { headers }).pipe(
          tap(() => {
            this.historyData.set(
              `User Administrator Created`,
              "usersAdmin",
              "Create",
              user,
            );
          }));
      }),
    );
  }

  public deleteAdministratorUser(userId: string, user): Observable<any> {
    return this.authService.getTokenUser().pipe(
      switchMap((token: string) => {
        if (!token) {
          return of([]);
        }
        const headers = new HttpHeaders({ Authorization: token });
        return this.http.post(`${ENV.backend}/admin/update`,
          {
            email: user.email,
            isInactive: new Date(),
          }, {
          headers,
        }).pipe(tap(() => {
          this.historyData.set(
            `User Administrator Deleted`,
            "usersAdmin",
            "Delete",
            user,
          );
        }));
      }),
    );
  }

  public createUsersFromObject(
    users: any[],
    client: string,
    password: string = null,
    passwordCustom: string = null,
    networkingState: boolean = false,
  ) {
    return this.http.post(
      `${ENV.backend}/users/createMultipleUsers`,
      { client, users, password, passwordCustom, networkingState },
    );
  }

  public updateUserAdministratorInformation(user: IAdministratorUser): Observable<any> {
    return this.authService.getTokenUser().pipe(
      switchMap((token: string) => {
        if (!token) {
          return of([]);
        }
        const headers = new HttpHeaders({ Authorization: token });

        const sendUser = user;
        delete (sendUser.confirmPassword);
        delete (sendUser.password);

        return this.http.post(`${ENV.backend}/admin/update`, { ...sendUser }, { headers }).pipe(
          tap(() => {
            this.historyData.set(
              `User Administrator Update`,
              "usersAdmin",
              "Create",
              user,
            );
          }));
      }),
    );
  }

  public getJSONUsers(clientId: string, eventId: string = null) {
    let httpObs;
    if (eventId) {
      httpObs = this.http.get(`${ENV.backend}/users/events/get/${eventId}`);
    } else {
      httpObs = this.http.get(`${ENV.backend}/users/${clientId}`);
    }

    return httpObs;
  }

  /**
   * Carga masiva de usuarios
   *
   * @param clientId
   * @param processId
   * @param url
   * @param updateUser
   * @param updatePass
   * @param isNetworking
   * @param terms
   * @param interest
   * @param genericPass
   */
  public sendProcessBackEnd(
    clientId: string,
    processId: string,
    url: string,
    updateUser: boolean,
    updatePass: boolean,
    isNetworking: boolean,
    terms: boolean,
    interest: any,
    genericPass: string = null,
  ) {
    const headers = new HttpHeaders().set("Content-Type", "application/json");

    return new Promise((resolve, reject) => {
      this.http
        .post(`${ENV.backend}/users/massive/${clientId}/${processId}`, {
          genericPass,
          interest,
          isNetworking,
          terms,
          updatePass,
          updateUser,
          url,
        }, { headers })
        .subscribe((data: any) => {
          resolve(data);
        }, (error) => { reject(error); });
    });
  }

  public addPaymentUser(url, client) {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return new Promise((resolve, reject) => {
      this.http.post(`${ENV.backend}/clubs/`, {
        client,
        override: false,
        url,
      }, { headers })
        .subscribe((data: any) => {
          resolve(data);
        }, (error) => { reject(error); });
    });
  }

  public sendNotificationUser(user, template = this.defaultTemplate) {
    const headers = new HttpHeaders()
      .set("Content-Type", "application/json");
    return this.http.post(`${ENV.backend}/notification/mail`,
      {
        from: "info@innpulsacolombia.com",
        params: {
          contraseña: user.password,
          nombre_usuario: user.name,
          usuario: ENV.mode === "Development" ? "soporte@rebus.com.co" : user.email,
        },
        subjet: ENV.mode === "Development" ? "PRUEBA NOTIFICACION" : "Registro Heroesfest.co",
        template,
        to: [user.email],
      }
      , {
        headers,
      });
  }

  public async getUsersStats(clientId) {
    const token = await this.authService.getTokenUser().pipe(take(1)).toPromise();
    const headers = new HttpHeaders({ Authorization: token });
    return new Promise((resolve, reject) => {
      this.http.get(`${ENV.backend}/users/v1/stats_client/${clientId}`, { headers })
        .subscribe((data: any) => {
          resolve(data);
        }, (error) => { reject(error); });
    });
  }

  public async uploadMassiveProcess(url, clientId, path, typeFile) {
    const token = await this.authService.getTokenUser().pipe(take(1)).toPromise();
    const headers = new HttpHeaders({ Authorization: token });
    const reference = typeFile === 'json' ? `${ENV.backend}/util/jsonload` : `${ENV.backend}/util/csv`
    return new Promise((resolve, reject) => {
      this.http.post(`${reference}`, {
        client: clientId,
        url,
        path
      }, { headers })
        .subscribe((data: any) => {
          resolve(data);
        }, (error) => { reject(error); });
    });
  }

  public async deleteMassiveProcess(clientId, path) {
    const token = await this.authService.getTokenUser().pipe(take(1)).toPromise();
    const headers = new HttpHeaders({ Authorization: token });
    return new Promise((resolve, reject) => {
      this.http.post(`${ENV.backend}/util/delete/jsonload`, {
        client: clientId,
        path
      }, { headers })
        .subscribe((data: any) => {
          resolve(data);
        }, (error) => { reject(error); });
    });
  }

  public preUploadMassiveProcess(url, clientId, path, typeFile, id: string = '') {
    id = this.afDb.createPushId();
    const created_at = firebase.database.ServerValue.TIMESTAMP;
    const uploadMasive = {
      "id": id,
      "createdAt": created_at,
      "url": url,
      "path": path,
      "typeFile": typeFile,
      "state": 'pending'
    };

    let reference = `/Clients/${clientId}/MassiveLoadPending`;

    const result = this.afDb.object(`${reference}/${id}`).update(uploadMasive).then(() => uploadMasive)
      .catch((error) => {
        this.utilities.consoleDecorator('Whooops, something happen', error);
      });
    return result;
  }

  public getUploadsMassivePending(clientId: string) {
    let ref = `Clients/${clientId}/MassiveLoadPending`;
    return this.afDb.list<any>(ref).valueChanges();
  }

  public updateStateMassiveProcess(processId, clientId, user) {
    return this.afDb.object(`Clients/${clientId}/MassiveLoadPending/${processId}`)
      .update({ state: 'approved', userNameApproved: user.name });
  }

  public sendReportUsers(client: any, emailUser: string, zone: string, eventId: string = null): Observable<any> {
    return this.authService.getTokenUser().pipe(
      switchMap((token: string) => {
        if (!token) {
          return of([]);
        }
        const headers = new HttpHeaders({ Authorization: token });

        // return this.http.post(`${ENV.jobs_url}/v1/reports/users`,
        return this.http.post(`${ENV.backend}/reports/generate_users`,
          {
            zone,
            eventId,
            clientId: client.id,
            email: emailUser,
            subject: 'Reporte de usuarios y conexiones - ' + client.name
          },
          { headers });
      }),
    );
  }
  public sendReportTrackingUsers(client: any, emailUser: string, zone: string, eventId: string = null): Observable<any> {
    return this.authService.getTokenUser().pipe(
      switchMap((token: string) => {
        if (!token) {
          return of([]);
        }
        const headers = new HttpHeaders({ Authorization: token });

        return this.http.post(`${ENV.backend}/reports/generate_tracking`,
        // return this.http.post(`${ENV.jobs_url}/v1/reports/tracking`,
          {
            zone,
            eventId,
            clientId: client.id,
            email: emailUser,
            subject: 'Reporte de usuarios y tracking - ' + client.name
          },
          { headers });
      }),
    );
  }

  public sendReportTagsUsers(client: any, emailUser: string): Observable<any> {
    return this.authService.getTokenUser().pipe(
      switchMap((token: string) => {
        if (!token) {
          return of([]);
        }
        const headers = new HttpHeaders({ Authorization: token });

        // return this.http.post(`${ENV.jobs_url}/v1/reports/tags`,
        return this.http.post(`${ENV.backend}/reports/generate_tags`,
          {
            clientId: client.id,
            email: emailUser,
            subject: 'Reporte de tags - Campañas UTM - ' + client.name
          },
          { headers });
      }),
    );
  }

  public logoutMassive(client: any,): Observable<any> {
    return this.authService.getTokenUser().pipe(
      switchMap((token: string) => {
        if (!token) {
          return of([]);
        }
        const headers = new HttpHeaders({ Authorization: token });

        return this.http.post(`${ENV.backend}/auth/relogin`,
          { "clientId": client.id, },
          { headers });
      }),
    );
  }
}
 // end file



// WEBPACK FOOTER //
// ./src/providers/user.ts